import { format, addDays, subDays } from 'date-fns'
import LayerTooltip from '../components/map/dialog/LayerToolTip'

// Sentinel Hub rejects output images larger than 2500 x 2500 pixels
const MAX_IMAGE_DIMENSION = 2500
// Our metre estimate is slightly smaller than Sentinel Hub's UTM-based size
// (~1% here), so fall back before getting close to the limit
const MAX_IMAGE_DIMENSION_SAFETY = 0.95
const METERS_PER_DEGREE = 111320

// Estimates the output image size (in pixels) a RESX/RESY request would produce.
// The stored image_url uses WMS 1.3.0 + EPSG:4326, so BBOX is minLat,minLon,maxLat,maxLon.
function estimateImageDimensions(imageUrl, resx, resy) {
  const bboxParam = imageUrl.split('&').find((pr) => pr.startsWith('BBOX='))
  if (!bboxParam) return null

  const [minLat, minLon, maxLat, maxLon] = decodeURIComponent(
    bboxParam.slice('BBOX='.length).replace(/\+/g, ' ')
  )
    .split(',')
    .map(Number)
  const resxMeters = parseFloat(resx)
  const resyMeters = parseFloat(resy)
  if (
    [minLat, minLon, maxLat, maxLon, resxMeters, resyMeters].some(
      (value) => !Number.isFinite(value)
    )
  )
    return null

  const midLat = ((minLat + maxLat) / 2) * (Math.PI / 180)
  const widthMeters = (maxLon - minLon) * METERS_PER_DEGREE * Math.cos(midLat)
  const heightMeters = (maxLat - minLat) * METERS_PER_DEGREE

  return {
    width: widthMeters / resxMeters,
    height: heightMeters / resyMeters,
  }
}

function insertResolutionToURL(imageUrl, selectedResolution) {
  const { RESX, RESY } = selectedResolution.config_aux
  const url = imageUrl
    .split('&')
    .filter((pr) => !pr.startsWith('HEIGHT'))
    .filter((pr) => !pr.startsWith('WIDTH'))
    .join('&')

  const dimensions = estimateImageDimensions(imageUrl, RESX, RESY)
  const maxDimension = MAX_IMAGE_DIMENSION * MAX_IMAGE_DIMENSION_SAFETY
  if (
    dimensions &&
    (dimensions.width > maxDimension || dimensions.height > maxDimension)
  ) {
    // Too large at the native resolution: request the largest allowed image
    // with the same aspect ratio instead (slightly coarser than RESX/RESY)
    const scale =
      MAX_IMAGE_DIMENSION / Math.max(dimensions.width, dimensions.height)
    const width = Math.max(1, Math.floor(dimensions.width * scale))
    const height = Math.max(1, Math.floor(dimensions.height * scale))
    return `${url}&WIDTH=${width}&HEIGHT=${height}`
  }

  return `${url}&RESX=${RESX}&RESY=${RESY}`
}

function computeFilteredEvalScript(indexFilter) {
  return `EVALSCRIPTURL=${indexFilter.EVALSCRIPTSURL}`
}

export function createImageUrl(
  parcel,
  layer,
  selectedResolution,
  indexFilter = null,
  toDate = addDays(new Date(), 3)
) {
  const rtime =
    layer.name !== 'ANOMALY_MEAN_NDVI'
      ? `TIME=${format(subDays(toDate, 15), 'yyyy-MM-dd')}/${format(
        toDate,
        'yyyy-MM-dd'
      )}`
      : `TIME=${format(toDate, 'yyyy-MM-dd')}`
  if (
    selectedResolution.config_aux?.RESX &&
    selectedResolution.config_aux?.RESY
  ) {
    return `${insertResolutionToURL(
      parcel.properties.image_url,
      selectedResolution
    )}&LAYERS=${layer.name}&${rtime}&${indexFilter && layer.eval_script
      ? computeFilteredEvalScript(indexFilter)
      : ''
      }`
  }
  return `${parcel.properties.image_url}&LAYERS=${layer.name}&${rtime}&${indexFilter && layer.eval_script
    ? computeFilteredEvalScript(indexFilter)
    : ''
    }`
}

export function createImageUrlWithDate(
  parcel,
  layer,
  date,
  selectedResolution,
  indexFilter = null
) {
  const rtime =
    layer.name !== 'ANOMALY_MEAN_NDVI'
      ? `TIME=${date}/${date}`
      : `TIME=${date}`
  if (
    selectedResolution.config_aux?.RESX &&
    selectedResolution.config_aux?.RESY
  )
    return `${insertResolutionToURL(
      parcel.properties.image_url,
      selectedResolution
    )}&LAYERS=${layer.name}&${rtime}&${indexFilter && layer.eval_script
      ? computeFilteredEvalScript(indexFilter)
      : ''
      }`
  return `${parcel.properties.image_url}&LAYERS=${layer.name}&${rtime}&${indexFilter && layer.eval_script
    ? computeFilteredEvalScript(indexFilter)
    : ''
    }`
}

export function createImageCompeleteUrlWithDate(
  parcel,
  layer,
  date,
  selectedResolution,
  stats = undefined
) {
  const imageUrl = `${parcel.properties.image_url}&LAYERS=${layer.name}&TIME=${date}`
  const url = `${process.env.REACT_APP_BASE_URL_IMAGES}${selectedResolution.instance_id}?`

  return {
    parcelUrl: `${url}${imageUrl}`,
    parcel: parcel,
    stats: stats,
    layer: layer,
  }
}

export function generateGetFeatureInfoLink(e, baseUrl, layer, parcelUrl) {
  const urlParams = parcelUrl
    .split('&')
    .filter((param) => param.startsWith('TIME='))

  const bbox = e.target.getBounds().toBBoxString()
  const width = e.target._image.clientWidth
  const height = e.target._image.clientHeight
  const i = Math.floor(e.originalEvent.offsetX)
  const j = Math.floor(e.originalEvent.offsetY)

  let link = `${baseUrl}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetFeatureInfo&BBOX=${bbox}&CRS=CRS:84&WIDTH=${width}&HEIGHT=${height}&LAYERS=${layer.layer_stats}&FORMAT=image/png&QUERY_LAYERS=${layer.layer_stats}&INFO_FORMAT=application/json&I=${i}&J=${j}`

  if (urlParams.length === 1) link = `${link}&${urlParams[0]}`

  return link
}

export function generateGetFeatureInfoLink1(e, clientHeight, clientWidth, baseUrl, layer) {
  const bbox = e.target.getBounds().toBBoxString()
  const i = Math.floor(e.originalEvent.offsetX)
  const j = Math.floor(e.originalEvent.offsetY)
  const pointGeometry = `POINT(${e.latlng.lng} ${e.latlng.lat})`;
  const latlng = e.latlng;

  // Create a circular buffer around the clicked point
  const bufferRadius = 0.001; // Adjust the buffer radius as needed
  const bufferPolygon = L.circle(latlng, { radius: bufferRadius }).toGeoJSON();
  const wktBufferPolygon = `POLYGON((${bufferPolygon.geometry.coordinates[0]}, ${bufferPolygon.geometry.coordinates[1]}))`

  const ciao = e.latlng.toBounds(0.001)
  const filter = `<Filter><Intersects><PropertyName>geometry</PropertyName><Point><coordinates>${e.latlng.lng},${e.latlng.lat}</coordinates></Point></Intersects></Filter>`;

  let link = `${baseUrl}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetFeatureInfo&BBOX=${bbox}&CRS=CRS:84&WIDTH=${clientWidth}&HEIGHT=${clientHeight}&LAYERS=${layer.layer_stats}&FORMAT=image/png&QUERY_LAYERS=${layer.layer_stats}&INFO_FORMAT=application/json&I=${i}&J=${j}`
  let link1 = `https://services.sentinel-hub.com/ogc/wfs/c71e45fa-b014-4e93-9da6-d189b8082807?SERVICE=WFS&REQUEST=GetFeature&BBOX=${bbox}&SRSNAME=CRS:84&WIDTH=${clientWidth}&HEIGHT=${clientHeight}&LAYER=${layer.layer_stats}&FORMAT=image/png&OUTPUTFORMAT=application/json&TYPENAMES=byoc-a802d2fb-0d2d-47cc-949d-70c50a08731d&GEOMETRY=${wktBufferPolygon}`
  let link2 = `https://services.sentinel-hub.com/ogc/wfs/c71e45fa-b014-4e93-9da6-d189b8082807?SERVICE=WFS&REQUEST=GetFeature&BBOX=${bbox}&SRSNAME=CRS:84&WIDTH=${clientWidth}&HEIGHT=${clientHeight}&LAYERS=${layer.layer_stats}&OUTPUTFORMAT=application/json&TYPENAMES=byoc-a802d2fb-0d2d-47cc-949d-70c50a08731d&FILTER=${filter}`
  return link
}
